import { useCallback, useEffect, useMemo, useRef } from 'react'

const useDebounce = <Args extends unknown[]>(
  func: (...args: Args) => void,
  delay = 1000,
) => {
  const callbackRef = useRef(func)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    callbackRef.current = func
  }, [func])

  const cancel = useCallback(() => {
    clearTimeout(timerRef.current)
    timerRef.current = undefined
  }, [])

  useEffect(() => cancel, [cancel, delay])

  return useMemo(() => {
    const debouncedFunction = (...args: Args) => {
      cancel()
      timerRef.current = setTimeout(() => {
        timerRef.current = undefined
        callbackRef.current(...args)
      }, delay)
    }
    debouncedFunction.cancel = cancel
    return debouncedFunction
  }, [cancel, delay])
}

export default useDebounce
